import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { addDiscountToProduct } from '../addDiscountToProduct';
import { getProductDiscounts } from '../getProductDiscounts';
import { updateProductDiscount } from '../updateProductDiscount';
import { removeDiscountFromProduct } from '../removeDiscountFromProduct';
import { useDialog } from '../../../context/Dialog';
import useIonicToast from '../../../hooks/useIonicToast';
import { addProductImage } from '../addProductImages';
import { updateProduct } from '../updateProduct';
import { ProductInformation } from '../../../types/Products/product';
import { removeProductImage } from '../removeProductImage';
import ActionConfirmationDialog from '../../../components/ui/ActionConfirmationDialog';
import { editProductSchema } from '../../../schemas/newProductSchema';

export const useProductEditor = (
  initialFormState: Partial<ProductInformation>
) => {
  const [step, setStep] = useState<number>(0);
  const handleNext = () => setStep(step + 1);
  const { setBusy, closeDialog, openDialog } = useDialog();
  const { presentIonToast } = useIonicToast();
  const query = useQueryClient();
  const { authToken, storeId } = useSelector((state: RootState) => ({
    authToken: state.userSession.signedAccessToken,
    storeId: state.userStores.activeStore?.id,
  }));

  const invalidateProductList = async () =>
    query.invalidateQueries({ queryKey: ['products-catalog'], exact: false });

  const initialFormValues: z.infer<typeof editProductSchema> | undefined =
    useMemo(() => {
      const res = initialFormState;
      if (res)
        return {
          ...res,
          price: Number(res.price), //since price is sent as a string from the backend
          category: { id: res.category_id, name: res.category },
          hash_tag: res.hash_tags,
          isDiscounted:
            res.discount_percentages && res.discount_percentages?.length > 0,
          existing_images: res.images,
          discount_amount: res.discount_percentages?.[0],
          quantity: res.quantity || 0,
        } as unknown as z.infer<typeof editProductSchema>;
      return;
    }, []);

  const methods = useForm<z.infer<typeof editProductSchema>>({
    resolver: zodResolver(editProductSchema),
    mode: 'all',
    defaultValues: initialFormValues,
  });

  const { dirtyFields, isDirty } = methods.formState;

  // Reconciles the product's single flat discount with what the form asks for.
  // Pass `undefined` as the percentage to clear any discount on the product.
  const syncProductDiscount = async (
    productId: number | undefined,
    percentage: number | undefined
  ) => {
    if (!authToken || !productId) return;

    const existing = await getProductDiscounts(productId, authToken);

    if (!percentage) {
      await Promise.all(
        (existing ?? []).map(({ id }) =>
          removeDiscountFromProduct(id, authToken)
        )
      );
      return;
    }

    const [current, ...duplicates] = existing ?? [];

    if (current) {
      if (current.discount_percentage !== percentage) {
        await updateProductDiscount(
          current.id,
          { discount_percentage: percentage },
          authToken
        );
      }
      // Older builds could leave more than one row behind; keep just the first.
      await Promise.all(
        duplicates.map(({ id }) => removeDiscountFromProduct(id, authToken))
      );
      return;
    }

    await addDiscountToProduct(
      productId,
      {
        discount_percentage: percentage, //since it is suppose to be in percentage
        product_id: productId,
      },
      authToken
    );
  };

  //HANDLE PRODUCT CREATION
  const editProductInfo = useMutation({
    mutationFn: async () => {
      if (!authToken || !storeId) return;
      try {
        setBusy(true);
        const values = methods.getValues();
        if (!values.id) return;

        const res = await updateProduct(
          values.id,
          {
            category: values.category.id,
            description: values.description,
            hash_tag: values.hash_tag.map(({ id }) => id),
            name: values.name,
            price: values.price,
            quantity: values.quantity,
          },
          authToken,
          storeId
        );

        //SEND REQUEST TO DISCOUNT THE PRODUCT PRICE
        // The product already has a discount row whenever it was discounted
        // before, and the backend rejects a second row whose quantity range
        // overlaps. So reconcile against what's stored: update the existing
        // row, add one when there is none, and delete when the toggle is off.
        if (dirtyFields.discount_amount || dirtyFields.isDiscounted) {
          await syncProductDiscount(
            res?.id ?? values.id,
            values.isDiscounted ? values.discount_amount : undefined
          );
        }
        await invalidateProductList();
        presentIonToast('Product information successfully updated');
      } catch (error: any) {
        presentIonToast(error.message, 'error');
      } finally {
        setBusy(false);
      }
    },
  });

  const setProductImage = useMutation({
    mutationFn: async () => {
      try {
        const { id, new_images, existing_images } = methods.getValues();
        if (!new_images || new_images?.length < 1) return;

        setBusy(
          true,
          `Attaching ${new_images.length > 1 ? 'images' : 'image'} to product...`
        );
        if (!authToken || !id) return;

        // Every product needs a default image — that is the one the storefront
        // cards render. If the edit left the product with no images at all,
        // promote the first replacement rather than uploading four non-defaults.
        const productImages = new_images.map((file, idx) => ({
          file,
          is_default: idx === 0 && (existing_images?.length ?? 0) === 0,
        }));

        await addProductImage({
          id,
          files: productImages,
          authToken: authToken,
        });

        presentIonToast(
          `${new_images.length > 1 ? 'images' : 'image'} successfully attached`
        );
        await invalidateProductList();
        closeDialog();
      } catch (error: any) {
        console.error(error.message);
        presentIonToast(error.message, 'error');
        throw error;
      } finally {
        setBusy(false);
      }
    },
  });

  //HANDLES SUBMISSION OF CURRENT STEP
  const handleStepOneSubmission = async () => {
    if (!isDirty) return handleNext();
    const isValid = await methods.trigger([
      'name',
      'category',
      'hash_tag',
      'price',
      'quantity',
      'description',
      'discount_amount',
      'discount_starts_at',
      'discount_ends_at',
    ]);
    if (!isValid) return;
    try {
      await editProductInfo.mutateAsync();
      handleNext(); // Move to next step only after successful mutation
    } catch (error) {
      // Error is already handled in mutation error handler
      console.error('Product creation failed:', error);
    }
  };

  const handleStepTwoSubmission = async () => {
    const { existing_images, new_images } = methods.getValues();
    const numberOfImgsLeft =
      4 - (existing_images.length + (new_images?.length || 0));
    if (numberOfImgsLeft > 0)
      return presentIonToast(
        `You need to upload ${numberOfImgsLeft} more image${numberOfImgsLeft > 1 && 's'} for this product`,
        'error'
      );

    if (!dirtyFields.new_images && !dirtyFields.existing_images) {
      presentIonToast('Product updated successfully');
      closeDialog({ keepParent: false });
      return;
    }
    const isValid = await methods.trigger(['new_images', 'id']);
    if (!isValid) return;

    try {
      await setProductImage.mutateAsync();
    } catch (error) {
      // Error is already handled in mutation error handler
      console.error('Image upload failed:', error);
    }
  };

  // MAIN MECHANISM FOR REMOVING
  const removeImage = async (imageId: number) => {
    const { id: productId, existing_images, save_as } = methods.getValues();
    if (!authToken || !imageId || !storeId) return;
    if (!productId)
      return presentIonToast(
        'This image is not attached to any product.',
        'error'
      );
    try {
      setBusy(true);
      await removeProductImage(productId!, imageId, authToken);
      // IMAGES CURRENTLY STILL IN THE SYSTEM
      const currentUploads = existing_images.filter(({ id }) => id !== imageId);
      methods.setValue('existing_images', currentUploads);
      presentIonToast('image successful removed.');

      if (currentUploads.length < 4 && save_as === 'published') {
        await updateProduct(
          productId,
          { save_as: 'draft' },
          authToken,
          storeId
        );
        methods.setValue('save_as', 'draft');
      }
      await invalidateProductList();
      closeDialog({ keepParent: true });
    } catch (error: any) {
      presentIonToast(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const onImageRemoval = (imageId: number) => {
    return openDialog(
      <ActionConfirmationDialog
        onCancel={() => closeDialog({ keepParent: true })}
        onConfirm={() => removeImage(imageId)}
        confirmText="Delete"
        title="Remove image"
        message="Are you sure you want to remove this image"
      />,
      { allowBackgroundDismiss: false }
    );
  };

  //cancel form
  const onCancel = () => closeDialog();

  return {
    methods,
    handleStepOneSubmission,
    handleStepTwoSubmission,
    handleNext,
    onCancel,
    onImageRemoval,
    currentStep: step,
  };
};
